import React, { useEffect } from 'react';
import { updateSocialMeta } from '../utils/seo';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Check, Star, Users, Calendar, Gift } from 'lucide-react';

const Memberships = () => {
  useEffect(() => {
    document.title = 'Memberships - Pin High Golf & Lounge | Unlimited Golf Access in Bay Village';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Join Pin High Golf & Lounge with exclusive membership benefits. Unlimited play, priority booking, member rates, and year-round access to indoor golf simulators in Bay Village, Ohio.');
    }

    // Update canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://pinhighbay.com/memberships');
    updateSocialMeta({
      title: 'Memberships - Pin High Golf & Lounge | Unlimited Golf Access in Bay Village',
      description: 'Join Pin High Golf & Lounge with exclusive membership benefits. Unlimited play, priority booking, member rates, and year-round access to indoor golf simulators in Bay Village, Ohio.',
      path: '/memberships',
    });
  }, []);

  const membershipTiers = [
    {
      name: 'Birdie Membership',
      price: '$150',
      period: ' / month',
      semiAnnualDisplay: '$750 / semi-annual',
      annualDisplay: '$1,500 / year',
      description: 'Great option for the casual golfer who wants regular access',
      icon: Users,
      checkoutUrlMonthly: 'https://checkout.square.site/merchant/MLTWKYCV1T83N/checkout/3XHTMBPWQHIQIVKCAK7O6BKG',
      checkoutUrlSemiAnnual: 'https://square.link/u/3fcjce84',
      checkoutUrlAnnual: 'https://checkout.square.site/merchant/MLTWKYCV1T83N/checkout/PMORBDM2XPFG7QPPR26YB72D',
      features: [
        '8 hours of simulator time per month (designated hours)',
        'Ideal for weekly practice sessions or one to two rounds of 18-hole golf',
        '14-day advance booking window',
        'Bring one guest at no additional charge for up to two sessions per month',
        '10% off additional simulator time beyond the monthly allotment',
        '10% off all food and beverages for the member',
        '10% off pro shop merchandise',
        '10% tournament entry fees',
        '10% off all party room and event space rentals',
      ],
      color: 'blue',
    },
    {
      name: 'Eagle Membership',
      price: '$250',
      period: ' / month',
      semiAnnualDisplay: '$1,250 / semi-annual',
      annualDisplay: '$2,500 / year',
      description: 'For the serious golfer who wants to maximize simulator time',
      icon: Star,
      checkoutUrlMonthly: 'https://checkout.square.site/merchant/MLTWKYCV1T83N/checkout/L2OZKKJI5HWMQIDMUXN2GGHL',
      checkoutUrlSemiAnnual: 'https://square.link/u/7JOAkik5',
      checkoutUrlAnnual: 'https://checkout.square.site/merchant/MLTWKYCV1T83N/checkout/YTUVM5YCRD64KW7LKSUNIFP5',
      features: [
        'Unlimited golf simulator play during designated member hours: 8:00 AM to 3:00 PM, Monday through Friday',
        '21-day advance booking window to secure your preferred tee times, especially during peak hours',
        'Bring up to three guests at no additional charge for up to four sessions per month',
        'One dedicated, secure golf bag storage spot included',
        '20% off additional simulator time beyond the monthly allotment',
        '10% off all food and beverages for the member',
        '15% off pro shop merchandise',
        '15% off at Wolf & Co, Wine Dive CIC, The Arcade On Detroit, and Lakewood Truck Park (Bar Only)',
        '20% tournament entry fees',
        '10% off all party room and event space rentals',
      ],
      color: 'green',
      featured: true,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-green-600 to-green-800 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1762950891047-71950c788609)', backgroundSize: 'cover', backgroundPosition: 'center' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Unlock Unlimited Access
          </h1>
          <p className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto text-green-50">
            Our membership tiers are designed to fit every kind of golfer—from casual swing-seekers to dedicated competitors. Zero weather dependence, maximum enjoyment.
          </p>
        </div>
      </section>

      {/* Membership Tiers */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {membershipTiers.map((tier, index) => {
              const IconComponent = tier.icon;
              return (
                <Card 
                  key={index}
                  className={`relative border-2 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 ${
                    tier.featured ? 'border-green-500 scale-105 md:scale-110' : 'border-gray-200'
                  }`}
                >
                  {tier.featured && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-green-600 text-white px-4 py-1 rounded-full text-sm font-bold">
                        MOST POPULAR
                      </span>
                    </div>
                  )}
                  <CardHeader className={`text-center pb-8 ${
                    tier.featured ? 'bg-gradient-to-br from-green-50 to-white' : 'bg-white'
                  }`}>
                    <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                      {tier.name}
                    </CardTitle>
                    <p className="text-sm text-gray-600 mb-4">{tier.description}</p>
                    <div className="flex items-baseline justify-center mb-2">
                      <span className="text-5xl font-bold text-gray-900">{tier.price}</span>
                      <span className="text-gray-600 ml-2">{tier.period}</span>
                    </div>
                    {tier.semiAnnualDisplay && (
                      <p className="text-green-600 font-semibold text-sm mb-1">
                        {tier.semiAnnualDisplay}
                      </p>
                    )}
                    {tier.annualDisplay && (
                      <p className="text-green-600 font-semibold text-sm">
                        {tier.annualDisplay}
                      </p>
                    )}
                  </CardHeader>
                  <CardContent className="p-6">
                    <ul className="space-y-3 mb-6">
                      {tier.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-white" />
                          </div>
                          <span className="text-sm text-gray-700">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="space-y-3">
                      <Button 
                        asChild
                        className={`w-full ${
                          tier.featured 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-gray-900 hover:bg-gray-800 text-white'
                        } transition-colors`}
                        size="lg"
                      >
                        <a 
                          href={tier.checkoutUrlMonthly} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          Buy - {tier.price}{tier.period}
                        </a>
                      </Button>
                      <Button 
                        asChild
                        variant="outline"
                        className={`w-full border-2 ${
                          tier.featured 
                            ? 'border-green-600 text-green-600 hover:bg-green-50' 
                            : 'border-gray-900 text-gray-900 hover:bg-gray-50'
                        } transition-colors`}
                        size="lg"
                      >
                        <a 
                          href={tier.checkoutUrlSemiAnnual} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          Buy - {tier.semiAnnualDisplay}
                        </a>
                      </Button>
                      <Button 
                        asChild
                        variant="outline"
                        className={`w-full border-2 ${
                          tier.featured 
                            ? 'border-green-600 text-green-600 hover:bg-green-50' 
                            : 'border-gray-900 text-gray-900 hover:bg-gray-50'
                        } transition-colors`}
                        size="lg"
                      >
                        <a 
                          href={tier.checkoutUrlAnnual} 
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          Buy - {tier.annualDisplay}
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900">
              Why Become a Member?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Members enjoy priority booking, exclusive perks, priority access, and up to 3 free guests based on membership level.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Calendar className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Priority Booking</h3>
                <p className="text-gray-600">
                  Reserve your favorite time slots before they're available to the public.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Gift className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Exclusive Perks</h3>
                <p className="text-gray-600">
                  Member-only tournaments, social events, and special promotions throughout the year.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">Free Guests</h3>
                <p className="text-gray-600">
                  Bring a guest(s) at no additional charge - based on membership level
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-none shadow-2xl">
            <CardContent className="p-12 text-center">
              <div className="flex justify-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-8 h-8 text-yellow-400 fill-current" />
                ))}
              </div>
              <p className="text-2xl text-gray-700 mb-8 italic leading-relaxed">
                "I love my Eagle Membership because it finally feels like my golf routine is built around my life, not the other way around. With all the simulator time, priority booking, and being able to stash my clubs on-site, I can sneak in real practice, full rounds, and games with friends whenever it actually works for my schedule—no weather apps, no daylight worries, just golf when I want it."
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                  PW
                </div>
                <div className="text-left">
                  <p className="font-bold text-gray-900 text-lg">Paul W.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Join the Club?
          </h2>
          <p className="text-xl mb-8 text-gray-300">
            Stop by for a tour, or contact us to discuss which membership tier is right for you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              asChild
              size="lg" 
              className="bg-green-600 hover:bg-green-700 text-white text-lg px-8 py-6 transition-all duration-300"
            >
              <a href="/contact">Contact Us</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Memberships;